import { useEffect, useState } from "react";
import axios from "axios";
import PageShell from "../../components/public/PageShell";
import Achievements from "../../components/public/Achievements";
import Testimonials from "../../components/public/Testimonials";
import Clients from "../../components/public/Clients";
import usePageMeta from "../../hooks/usePageMeta";
import useReveal from "../../hooks/useReveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import {
  Target, Eye, HandHeart, Sparkle, ShieldCheck, Heartbeat,
  Flask, Leaf, Certificate, ArrowRight, Calendar,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Sensible defaults — admin can override via Pages keys
const DEFAULT_STORY = {
  heading: "Two decades of building medicines that matter",
  body: "Bonheur Pharmaceuticals was founded in 2003 with a simple conviction: that affordable, high-quality medicines should be available to every clinician who needs them. Two decades later, we manufacture across 12 therapeutic areas, serve 500+ healthcare partners, and ship to 12 international markets.\n\nWe are vertically integrated from R&D through manufacturing and distribution — every product carries the same care from the lab bench to the patient's bedside.",
  image_url: "",
};

const DEFAULT_MVV_DATA = {
  mission: "To make safe, effective and affordable medicines accessible to every clinician — and every patient they serve — across India and beyond.",
  vision:  "To be the most trusted Indian pharmaceutical partner of choice for clinicians by 2030 — known for science, supply consistency and patient outcomes.",
  values:  "Patients first. Science always. Promises kept. We hold ourselves to standards that are higher than what regulation requires.",
};

const MVV_META = [
  { key: "mission", icon: Target,    title: "Our mission" },
  { key: "vision",  icon: Eye,       title: "Our vision" },
  { key: "values",  icon: HandHeart, title: "Our values" },
];

const ICON_MAP = { Flask, ShieldCheck, Heartbeat, Leaf, Sparkle, Target, Eye, HandHeart, Certificate };

const DEFAULT_PILLARS = [
  { icon: "Flask",       title: "Research-led",      text: "An in-house R&D team that has filed 40+ formulation dossiers in the last decade." },
  { icon: "ShieldCheck", title: "Quality-obsessed",  text: "ISO 9001, WHO-GMP and EU-GMP certified facility audited annually by independent third parties." },
  { icon: "Heartbeat",   title: "Patient-centred",   text: "Affordability and accessibility are non-negotiable. Every product is priced for real-world Indian healthcare." },
  { icon: "Leaf",        title: "Sustainable by design", text: "Solvent recovery, low-waste manufacturing, and a journey toward carbon-neutral operations by 2030." },
];

const DEFAULT_MILESTONES = [
  { year: "2003", title: "Founded in Baddi", text: "Started with a single tablet line and 22 employees." },
  { year: "2008", title: "WHO-GMP certified", text: "Cleared first WHO-GMP inspection and began contract manufacturing for global majors." },
  { year: "2013", title: "Crossed 100 products", text: "Expanded portfolio across cardiology, diabetology and antimicrobials." },
  { year: "2017", title: "Export to 8 countries", text: "Filed dossiers in ASEAN, CIS and East Africa markets." },
  { year: "2021", title: "EU-GMP for solids", text: "Earned EU-GMP certification for our oral solid dosage block." },
  { year: "2024", title: "200 products, 12 markets", text: "Crossed 200 SKUs and onboarded our 500th healthcare partner." },
];

const DEFAULT_LEADERSHIP = [
  {
    name: "Rajiv Krishnan",
    role: "Founder & Managing Director",
    bio: "M.Pharm, IIT Bombay alum. Two decades of operating experience across formulations, regulatory affairs and commercial leadership.",
    image_url: "",
  },
  {
    name: "Dr. Anjali Mehta",
    role: "Chief Medical Officer",
    bio: "MD (Internal Medicine), PGI Chandigarh. Leads clinical affairs, medical writing and pharmacovigilance.",
    image_url: "",
  },
  {
    name: "Vivek Iyer",
    role: "Head — Manufacturing",
    bio: "B.Tech Chemical, 18 years across solid dosage, liquid orals and topicals. Architect of our Baddi facility.",
    image_url: "",
  },
  {
    name: "Karan Shah",
    role: "Head — Strategy & Exports",
    bio: "IIM Bangalore MBA. Builds long-term commercial partnerships across regulated and semi-regulated markets.",
    image_url: "",
  },
];

const DEFAULT_CERTIFICATIONS = [
  { name: "WHO-GMP", issuer: "World Health Organization", year: "Renewed 2024" },
  { name: "EU-GMP", issuer: "European Directorate", year: "2021–present" },
  { name: "ISO 9001:2015", issuer: "Quality Management Systems", year: "Certified 2018" },
  { name: "ISO 14001", issuer: "Environmental Management", year: "Certified 2020" },
  { name: "DCGI Approved", issuer: "Drug Controller General of India", year: "Active license" },
  { name: "USFDA Inspection", issuer: "US Food & Drug Administration", year: "Cleared 2023" },
];

export default function AboutPage() {
  usePageMeta({
    title: "About Bon-Heur Pharmaceuticals — Innovation is our DNA",
    description: "Bon-Heur Pharmaceuticals is a WHO-GMP certified manufacturer in Haridwar, founded in 2008 and serving clinicians across India with 1000+ commercialised formulations.",
    keywords: "About Bon-Heur Pharmaceuticals, Haridwar pharma manufacturer, WHO-GMP certified India, pharmaceutical company history",
  });

  const [story, setStory] = useState(DEFAULT_STORY);
  const [mvv, setMvv] = useState(DEFAULT_MVV_DATA);
  const [pillars, setPillars] = useState(DEFAULT_PILLARS);
  const [leadership, setLeadership] = useState(DEFAULT_LEADERSHIP);
  const [milestones, setMilestones] = useState(DEFAULT_MILESTONES);
  const [certs, setCerts] = useState(DEFAULT_CERTIFICATIONS);

  useEffect(() => {
    axios.get(`${API}/public/pages/home.about`).then((r) => {
      const v = r.data?.value;
      if (v) setStory((s) => ({ ...s, ...v }));
    }).catch(() => {});
    axios.get(`${API}/public/pages/about.mvv`).then((r) => {
      const v = r.data?.value;
      if (v && (v.mission || v.mission_body || v.vision || v.vision_body || v.values || v.values_body)) {
        setMvv((cur) => ({
          mission: v.mission_body || v.mission || cur.mission,
          vision:  v.vision_body  || v.vision  || cur.vision,
          values:  v.values_body  || v.values  || cur.values,
        }));
      }
    }).catch(() => {});
    axios.get(`${API}/public/pages/about.pillars`).then((r) => {
      const items = r.data?.value?.items;
      if (Array.isArray(items) && items.length) setPillars(items);
    }).catch(() => {});
    axios.get(`${API}/public/pages/about.leadership`).then((r) => {
      if (Array.isArray(r.data?.value?.items) && r.data.value.items.length) setLeadership(r.data.value.items);
    }).catch(() => {});
    axios.get(`${API}/public/pages/about.milestones`).then((r) => {
      if (Array.isArray(r.data?.value?.items) && r.data.value.items.length) setMilestones(r.data.value.items);
    }).catch(() => {});
    axios.get(`${API}/public/pages/about.certifications`).then((r) => {
      if (Array.isArray(r.data?.value?.items) && r.data.value.items.length) setCerts(r.data.value.items);
    }).catch(() => {});
  }, []);

  const storyImg = story.image_url
    ? (story.image_url.startsWith("http") ? story.image_url : `${BACKEND}${story.image_url}`)
    : DEFAULT_STORY.image_url;

  return (
    <PageShell
      pageKey="about"
      eyebrow="About us"
      title="Welcome to Bon-Heur Pharmaceuticals"
      subtitle="A WHO-GMP certified pharmaceutical company based in Haridwar — innovation is our DNA."
    >
      {/* STORY */}
      <Story story={story} storyImg={storyImg} />

      {/* MISSION / VISION / VALUES */}
      <MVVSection mvv={mvv} />

      {/* WHY BONHEUR — pillars */}
      <Pillars items={pillars} />

      {/* TIMELINE */}
      <Timeline items={milestones} />

      <Achievements />

      {/* LEADERSHIP */}
      <Leadership items={leadership} />

      {/* CERTIFICATIONS */}
      <Certifications items={certs} />

      <Testimonials />
      <Clients />
    </PageShell>
  );
}

function Story({ story, storyImg }) {
  const { ref, visible } = useReveal();
  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white" data-testid="about-story">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div
          className="lg:col-span-6 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-24px)" }}
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Our story
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2937] leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif" }}>{story.heading}</h2>
          <p className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed whitespace-pre-line">{story.body}</p>
        </div>
        <div
          className="lg:col-span-6 relative transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(24px)", transitionDelay: "150ms" }}
        >
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#0EA5E9]/10 rounded-2xl -z-10" aria-hidden />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#C2410C]/10 rounded-2xl -z-10" aria-hidden />
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#F1F5F9]">
            {storyImg ? (
              <img src={storyImg} alt="Inside Bonheur" className="w-full h-full object-cover" />
            ) : (
              <div
                aria-hidden
                className="w-full h-full"
                style={{
                  background:
                    "radial-gradient(120% 100% at 0% 0%, #C2410C22 0%, transparent 55%), radial-gradient(120% 100% at 100% 100%, #0EA5E933 0%, transparent 55%), linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MVVSection({ mvv }) {
  const { ref, visible } = useReveal();
  const cards = MVV_META.map((m) => ({ ...m, text: mvv[m.key] || DEFAULT_MVV_DATA[m.key] }));
  return (
    <section ref={ref} className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E2E8F0]" data-testid="about-mvv">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">What drives us</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Mission, vision and values</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((c, i) => (
            <div
              key={c.key}
              className="relative p-8 rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#C2410C] hover:shadow-md transition-all duration-300"
              style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "opacity 700ms, transform 700ms, border-color 300ms, box-shadow 300ms", transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C2410C] to-[#FB923C] flex items-center justify-center text-white">
                <c.icon size={22} weight="fill" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{c.title}</h3>
              <p className="mt-3 text-[#475569] leading-relaxed whitespace-pre-line">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pillars({ items }) {
  const { ref, visible } = useReveal();
  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white" data-testid="about-pillars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Why Bonheur</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2937] leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif" }}>Built on four pillars</h2>
          <p className="mt-4 text-[#475569]">A philosophy that shapes every product, partnership and process.</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((p, i) => {
            const IconComp = ICON_MAP[p.icon] || Flask;
            return (
              <div
                key={i}
                className="group p-7 rounded-2xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] hover:border-[#C2410C] transition-all duration-300"
                style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "opacity 700ms, transform 700ms, border-color 300ms", transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-[#0EA5E9]/10 flex items-center justify-center text-[#0369A1] group-hover:bg-[#C2410C]/10 group-hover:text-[#C2410C] transition-colors">
                  <IconComp size={20} weight="fill" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Timeline({ items }) {
  const { ref, visible } = useReveal();
  // Track which milestone is currently in viewport centre — displayed as massive
  // sticky background year for the "editorial parallax" effect.
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const nodes = document.querySelectorAll("[data-milestone-idx]");
    if (!nodes.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.4) {
            const idx = Number(e.target.getAttribute("data-milestone-idx"));
            if (!Number.isNaN(idx)) setActiveIdx(idx);
          }
        });
      },
      { rootMargin: "-30% 0px -30% 0px", threshold: [0.4, 0.6] }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [items]);
  return (
    <section ref={ref} className="py-20 lg:py-28 bg-[#0F172A] text-white relative overflow-hidden" data-testid="about-timeline">
      <div className="absolute inset-0 opacity-[0.05]" aria-hidden style={{
        backgroundImage: "radial-gradient(circle at 30% 20%, white 1px, transparent 1.5px)",
        backgroundSize: "44px 44px",
      }} />
      {/* Massive sticky year — pinned to viewport centre, ~20vw serif */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-[0.06] select-none" aria-hidden>
        <span key={items[activeIdx]?.year} className="font-display text-[22vw] leading-none text-white transition-opacity duration-500" data-testid="timeline-sticky-year">
          {items[activeIdx]?.year}
        </span>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#FB923C] font-semibold flex items-center gap-2">
            <Calendar size={12} weight="fill" /> Our journey
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif" }}>Milestones that shaped us</h2>
        </div>

        <div className="mt-16 relative">
          {/* Vertical line */}
          <div className="absolute left-3 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#FB923C]/40 via-white/20 to-[#FB923C]/40 -translate-x-1/2" aria-hidden />
          <div className="space-y-10">
            {items.map((m, i) => (
              <div
                key={i}
                data-milestone-idx={i}
                className={`relative grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-12 transition-all duration-700 ${i % 2 ? "sm:flex-row-reverse" : ""}`}
                style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transitionDelay: `${i * 80}ms` }}
              >
                {/* Dot */}
                <div className="absolute left-3 sm:left-1/2 top-3 w-3 h-3 rounded-full bg-[#FB923C] ring-4 ring-[#FB923C]/20 -translate-x-1/2" aria-hidden />
                {/* Even rows: year on left, content on right */}
                {i % 2 === 0 ? (
                  <>
                    <div className="pl-10 sm:pl-0 sm:text-right sm:pr-12">
                      <div className="text-3xl sm:text-5xl font-bold text-[#FB923C]" style={{ fontFamily: "'Manrope', sans-serif" }}>{m.year}</div>
                    </div>
                    <div className="pl-10 sm:pl-12">
                      <h3 className="text-lg font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>{m.title}</h3>
                      <p className="mt-2 text-white/70 leading-relaxed text-sm">{m.text}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pl-10 sm:pl-0 sm:order-2 sm:pl-12">
                      <div className="text-3xl sm:text-5xl font-bold text-[#FB923C]" style={{ fontFamily: "'Manrope', sans-serif" }}>{m.year}</div>
                    </div>
                    <div className="pl-10 sm:pl-0 sm:order-1 sm:text-right sm:pr-12">
                      <h3 className="text-lg font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>{m.title}</h3>
                      <p className="mt-2 text-white/70 leading-relaxed text-sm">{m.text}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Leadership({ items }) {
  const { ref, visible } = useReveal();
  const [selected, setSelected] = useState(null);
  const selectedImg = selected?.image_url && selected.image_url.startsWith("http")
    ? selected.image_url
    : (selected?.image_url ? `${BACKEND}${selected.image_url}` : null);
  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white" data-testid="about-leadership">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Leadership</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#0F172A] leading-[1.05]">The people behind every product</h2>
          <p className="mt-4 text-[#475569]">A team of scientists, builders and clinicians who care deeply about outcomes. Click any card to read the full bio.</p>
        </div>
        <div className={`mt-14 grid gap-6 lg:gap-8 grid-cols-2 ${items.length === 3 ? "md:grid-cols-3" : "lg:grid-cols-4"}`}>
          {items.map((p, i) => {
            const img = p.image_url && p.image_url.startsWith("http") ? p.image_url : (p.image_url ? `${BACKEND}${p.image_url}` : null);
            return (
              <button
                type="button"
                key={i}
                onClick={() => setSelected(p)}
                className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C2410C] rounded-2xl transition-transform hover:-translate-y-1 duration-300"
                style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "opacity 700ms, transform 700ms", transitionDelay: `${i * 90}ms` }}
                data-testid={`leadership-card-${i}`}
                aria-label={`Read full bio of ${p.name}`}
              >
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#F1F5F9]">
                  {img ? (
                    <img src={img} alt={p.name} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0EA5E9]/10 to-[#C2410C]/10 text-3xl font-bold text-[#94A3B8]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {p.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  {/* Read-more chip appears on hover */}
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-[10px] font-semibold text-[#0F172A] shadow-[0_2px_8px_rgba(0,0,0,0.12)] opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    Read more <ArrowRight size={10} weight="bold" />
                  </span>
                </div>
                <h3 className="mt-4 text-base font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.name}</h3>
                <div className="mt-0.5 text-xs text-[#C2410C] font-semibold tracking-wide">{p.role}</div>
                {p.bio && <p className="mt-2 text-xs text-[#64748B] leading-relaxed line-clamp-3">{p.bio}</p>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Full-bio dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="bg-white max-w-3xl p-0 overflow-hidden border-0 shadow-[0_30px_100px_rgba(15,23,42,0.25)]"
          data-testid="leadership-dialog"
        >
          {selected && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
              <div className="md:col-span-2 relative aspect-[4/5] md:aspect-auto bg-[#F1F5F9] overflow-hidden">
                {selectedImg ? (
                  <img src={selectedImg} alt={selected.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0EA5E9]/15 to-[#C2410C]/15 text-6xl font-display font-light text-[#94A3B8]">
                    {selected.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                )}
                {/* subtle saffron accent bar */}
                <span className="absolute left-0 top-0 h-1 w-16 bg-[#C2410C]" />
              </div>
              <div className="md:col-span-3 p-8 md:p-10 relative">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Leadership</div>
                <DialogTitle asChild>
                  <h3 className="mt-3 font-display text-3xl md:text-4xl font-light tracking-tighter text-[#0F172A] leading-[1.1]" data-testid="leadership-dialog-name">{selected.name}</h3>
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="mt-1 text-sm text-[#0EA5E9] font-semibold" data-testid="leadership-dialog-role">{selected.role}</div>
                </DialogDescription>
                {selected.bio && (
                  <p className="mt-6 text-[15px] leading-relaxed text-[#334155] whitespace-pre-line" data-testid="leadership-dialog-bio">
                    {selected.bio}
                  </p>
                )}
                {(selected.email || selected.linkedin) && (
                  <div className="mt-6 flex flex-wrap gap-3 text-xs">
                    {selected.email && <a href={`mailto:${selected.email}`} className="underline text-[#0F172A] hover:text-[#C2410C]">{selected.email}</a>}
                    {selected.linkedin && <a href={selected.linkedin} target="_blank" rel="noopener noreferrer" className="underline text-[#0F172A] hover:text-[#C2410C]">LinkedIn</a>}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Certifications({ items }) {
  const { ref, visible } = useReveal();
  return (
    <section ref={ref} className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E2E8F0]" data-testid="about-certifications">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold flex items-center gap-2">
              <Certificate size={12} weight="fill" /> Certifications & approvals
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Held to the highest standards</h2>
          </div>
          <a href="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#C2410C] hover:text-[#9A3412] group">
            Request our complete dossier <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((c, i) => (
            <div
              key={i}
              className="p-5 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#C2410C] hover:shadow-sm transition-all duration-300"
              style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)", transition: "opacity 600ms, transform 600ms, border-color 300ms, box-shadow 300ms", transitionDelay: `${i * 70}ms` }}
            >
              <Certificate size={18} weight="fill" className="text-[#C2410C]" />
              <div className="mt-3 text-sm font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{c.name}</div>
              {c.issuer && <div className="mt-1 text-[11px] text-[#64748B]">{c.issuer}</div>}
              {c.year && <div className="mt-1 text-[11px] text-[#94A3B8]">{c.year}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
